import { toJS } from 'mobx';
import { computedFn } from 'mobx-utils';
import numeral from 'numeral';

import type { Account, Campaign, OmniCampaign, Segment, Targetable, User } from '@feathr/blackbox';
import {
  CAMPAIGN_MAX_BUDGET_INTERNAL,
  CampaignClass,
  CampaignState,
  getMaxTargetValue,
} from '@feathr/blackbox';
import { moment } from '@feathr/hooks';

import type { ICampaignValidationErrors } from '../../../CampaignSummary';

export const getAudienceSize: (
  campaign: Campaign,
  segments: Segment[],
  targetables: Targetable[],
) => number = computedFn((campaign: Campaign, segments: Segment[], targetables: Targetable[]) => {
  const cls: CampaignClass = campaign.get('_cls');

  // Retargeting or Meta Retargeting
  if ([CampaignClass.Segment, CampaignClass.Facebook, CampaignClass.Omni].includes(cls)) {
    return segments.map((s) => s.reachable).reduce((acc, c) => acc + c, 0);
  }

  // Email Mapping or Meta Email Mapping
  if ([CampaignClass.EmailList, CampaignClass.EmailListFacebook].includes(cls)) {
    return targetables.reduce((acc, t) => {
      acc += t.get('num_unique_emails') || 0;
      return acc;
    }, 0);
  }

  // We use these numbers to scale the recommend budget by small, medium, and large expected audiences sizes
  switch (cls) {
    // Campaigns that target a specific audience are huge, so we limit the audience size to 100k to prevent an offensive recommended budget

    // Legacy Lookalike
    case CampaignClass.Lookalike:

    // Current Lookalike
    case CampaignClass.SeedSegment:

    case CampaignClass.Affinity: {
      return 100000;
    }

    // Search and Geo-like campaigns have smaller audiences on the order of 10s of thousands.

    // Search Keyword
    case CampaignClass.Search:

    // Historical Geofencing
    case CampaignClass.MobileGeoFencing:

    // Mobile Geofencing
    case CampaignClass.MobileGeoFenceRetargeting: {
      return 10000;
    }

    default: {
      // 1000 for default since first party groups tend to be pretty small
      return 1000;
    }
  }
});

// This calculation mirrors the incoming backend calculation for the number of days in a campaign
export function getDays(campaign: Campaign): number {
  const dateStart = campaign.get('date_start');
  const dateEnd = campaign.get('date_end');
  if (!(dateStart && dateEnd)) {
    return 0;
  }
  const startMoment = moment.utc(dateStart, moment.ISO_8601);
  const endMoment = moment.utc(dateEnd, moment.ISO_8601);

  const duration = moment.duration(endMoment.diff(startMoment));
  const hours = duration.asHours();
  const numDays = Math.ceil(hours / 24);

  return numDays;
}

export function getMinBudget(campaign: Campaign): number {
  const days = getDays(campaign);
  if (campaign.isMonetization) {
    return days * 4000;
  }

  return days * 5;
}

export const getRecommendedImpressions: (
  campaign: Campaign,
  segments: Segment[],
  targetables: Targetable[],
) => number = computedFn((campaign: Campaign, segments: Segment[], targetables: Targetable[]) => {
  // Impressions are just for monetization campaigns
  if (!campaign.isMonetization) {
    return 0;
  }
  const audienceSize = getAudienceSize(campaign, segments, targetables);
  const reach = 0.015;
  const freqCap = campaign.get('bidding_strategy').freq_cap;
  const days = getDays(campaign);
  return audienceSize * reach * freqCap * days;
});

export function getRecommendedBid(size: number): number {
  /*
   * This will be revisited at a later date. Intentionally leaving the multiple
   * if statements that evaluate to the same number.
   */
  if (size > 20000) {
    return 5;
  }
  if (size < 20000 && size > 5000) {
    return 5;
  }
  return 10;
}

interface IValidateStepBudgetProps {
  campaign: Campaign;
  account: Account;
  user?: User;
  noMaxBudget?: boolean;
  isOnProduction: boolean;
}

export function validateStepBudget({
  campaign,
  account,
  noMaxBudget,
  isOnProduction,
}: IValidateStepBudgetProps): ICampaignValidationErrors {
  const monetization = campaign.get('parent_kind') === 'partner';

  const attributes = ['date_start', 'date_end', 'state', '_cls'];
  if (monetization) {
    attributes.push('monetization_value');
  }
  if (campaign.isOmniCampaign) {
    attributes.push('exposure_settings');
  }

  const modelErrors = toJS(campaign.validate(attributes, false, 'grouped').errors) as Record<
    string,
    string[]
  >;

  const errors = {
    budget: [] as string[],
    channel_split: [] as string[],
    date_start: [] as string[],
    date_end: [] as string[],
    monetization_value: [] as string[],
    /*
     * We have to pass along state and _cls so they show up in the attributes provided
     * to the date_start validator
     */
    ...modelErrors,
  };

  /*
   * Map exposure_settings errors to channel_split so the UI can display them
   * under the ChannelBudgetSplit component.
   */
  if (modelErrors.exposure_settings?.length) {
    errors.channel_split = [...errors.channel_split, ...modelErrors.exposure_settings];
    delete (errors as Record<string, unknown>).exposure_settings;
  }

  /*
   * Additional guard: verify allocations are present for dual-channel Omni campaigns.
   * The model constraint checks field presence, but this also catches the edge case
   * where channel_split exists but has empty allocations.
   */
  if (campaign.isOmniCampaign && !errors.channel_split.length) {
    const omni = campaign as OmniCampaign;
    if (omni.isDualChannel) {
      const channelSplit = campaign.get('exposure_settings')?.channel_split;
      if (!channelSplit?.allocations?.length) {
        errors.channel_split.push('Configure a budget split between Display and Meta channels');
      }
    }
  }

  const canEditBudget = !!campaign.get('date_start') && !!campaign.get('date_end');
  const isDraft = campaign.get('state') === CampaignState.Draft;
  const budget = campaign.get('exposure_settings').target_value || 0;
  const minBudget = getMinBudget(campaign);
  const minRemainingBudget = getMinBudget(campaign) - budget;
  const spend: number = campaign.get('stats_cache')?.spend || 0;
  const exposureSettingsHaveChanged = campaign.isAttributeDirty('exposure_settings');
  // Campaign dates affect minimum budget calculation, so we need to validate when they change
  const campaignDatesHaveChanged =
    campaign.isAttributeDirty('date_start') || campaign.isAttributeDirty('date_end');
  /*
   *  Allow editing goals once spend has exceeded budget.
   *  https://github.com/Feathr/shrike/issues/1998
   *
   *  This previously did not take into account the state of the campaign,
   *  allowing the user to set a zero budget, save as draft, reload the
   *  page and publish with a zero budget.
   */
  const addErrorsAfterPublishedIfBudgetIsTouched =
    !isDraft && (exposureSettingsHaveChanged || campaignDatesHaveChanged);

  const isCapped = isOnProduction && account.get('is_internal');

  // Internal accounts are capped at $25 if on production to prevent spending real money accidentally
  if (isCapped && budget > CAMPAIGN_MAX_BUDGET_INTERNAL) {
    errors.budget.push(
      `Internal accounts can only create campaigns with budgets $${CAMPAIGN_MAX_BUDGET_INTERNAL} and under.`,
    );
  }

  if (
    budget < spend &&
    budget >= minBudget &&
    !monetization &&
    addErrorsAfterPublishedIfBudgetIsTouched
  ) {
    /*
     * Prevent user from setting a budget below campaign spend.
     * Do not include draft campaigns because a draft campaign
     * should never have spend associated with it.
     */
    errors.budget.push(
      `Budget must be at least $${numeral(spend).format(
        '0,0.00',
      )} to account for the amount already spent.`,
    );
  } else if (
    // Prevent budgets less than the minimum budget.
    budget < minBudget &&
    !monetization &&
    canEditBudget &&
    (addErrorsAfterPublishedIfBudgetIsTouched || isDraft) &&
    !isCapped
  ) {
    errors.budget.push(
      `Total budget must be at least ${numeral(minBudget).format('$0,0.00')}. Add ${numeral(
        minRemainingBudget,
      ).format('$0,0.00')} to fulfill this campaign for its duration.`,
    );
  }

  /*
   *We can ignore max budget under the following circumstances:
   *- The noMaxBudget feature flag is set
   */
  if (!noMaxBudget) {
    const maxBudget = getMaxTargetValue(account.campaignsMaxBudget, monetization);
    if (budget > maxBudget) {
      if (monetization) {
        errors.budget.push(
          `The number of impressions cannot exceed ${numeral(maxBudget).format('0,0')}.`,
        );
      } else {
        errors.budget.push(
          `The budget cannot exceed ${numeral(maxBudget).format('$0,0')} (account-wide setting).`,
        );
      }
    }
  }

  return errors;
}
